/**
 * Campos editables por nivel de edición de venta (espejo de `SaleEditFields` en pento-api-v2).
 * Acumulativos: cada nivel incluye los del anterior.
 * Cliente, método de cobro, número de pagos, frecuencia y suscripción
 * no pertenecen a ningún nivel: son inmutables tras el alta.
 */
export const SaleEditLevelEnum = {
  ADDITIONAL_ONLY: 'ADDITIONAL_ONLY',
  FISCAL_DATA: 'FISCAL_DATA',
  FULL: 'FULL'
} as const;

export type SaleEditLevel =
  (typeof SaleEditLevelEnum)[keyof typeof SaleEditLevelEnum];

export const SaleEditField = {
  NOTES: 'notes',
  ADDITIONAL_FIELDS: 'additionalFields',
  PAYMENT_METHOD_ID: 'paymentMethodId',
  PAYMENT_FORM_ID: 'paymentFormId',
  CFDI_USE_ID: 'cfdiUseId',
  AUTO_INVOICE: 'autoInvoice',
  CUSTOMER_ID: 'customerId',
  CURRENCY_ID: 'currencyId',
  COLLECTION_METHOD: 'collectionMethod',
  COLLECTION_START_DATE: 'collectionStartDate',
  DATE_OF_SALE: 'dateOfSale',
  DAYS_UNTIL_EXPIRY: 'daysUntilExpiry',
  PENALTY_FEE_AMOUNT: 'penaltyFeeAmount',
  PENALTY_INTEREST_RATE: 'penaltyInterestRate',
  NUMBER_OF_PAYMENTS: 'numberOfPayments',
  FREQUENCY: 'frequency',
  DISCOUNT: 'discount',
  SUBTOTAL: 'subtotal',
  TAX: 'tax',
  TOTAL: 'total',
  LINES: 'lines',
  STATUS: 'status',
  PROCESS_STATUS: 'processStatus',
  METADATA: 'metadata',
  SUBSCRIPTION_ID: 'subscriptionId'
} as const;

export type SaleEditFieldName = (typeof SaleEditField)[keyof typeof SaleEditField];

/** Tras el alta el API rechaza cualquier cambio de estos campos. */
export const SALE_IMMUTABLE_FIELDS = new Set<SaleEditFieldName>([
  SaleEditField.CUSTOMER_ID,
  SaleEditField.COLLECTION_METHOD,
  SaleEditField.NUMBER_OF_PAYMENTS,
  SaleEditField.FREQUENCY,
  SaleEditField.SUBSCRIPTION_ID
]);

const ADDITIONAL_ONLY = new Set<SaleEditFieldName>([
  SaleEditField.NOTES,
  SaleEditField.ADDITIONAL_FIELDS,
  SaleEditField.STATUS,
  SaleEditField.PROCESS_STATUS,
  SaleEditField.METADATA
]);

const FISCAL_DATA = new Set<SaleEditFieldName>([
  ...ADDITIONAL_ONLY,
  SaleEditField.DATE_OF_SALE,
  SaleEditField.PAYMENT_METHOD_ID,
  SaleEditField.PAYMENT_FORM_ID,
  SaleEditField.CFDI_USE_ID,
  SaleEditField.AUTO_INVOICE
]);

const FULL = new Set<SaleEditFieldName>([
  ...FISCAL_DATA,
  SaleEditField.CURRENCY_ID,
  SaleEditField.COLLECTION_START_DATE,
  SaleEditField.DAYS_UNTIL_EXPIRY,
  SaleEditField.PENALTY_FEE_AMOUNT,
  SaleEditField.PENALTY_INTEREST_RATE,
  SaleEditField.DISCOUNT,
  SaleEditField.SUBTOTAL,
  SaleEditField.TAX,
  SaleEditField.TOTAL,
  SaleEditField.LINES
]);

export const SALE_EDIT_FIELDS_BY_LEVEL: Record<SaleEditLevel, ReadonlySet<SaleEditFieldName>> = {
  [SaleEditLevelEnum.ADDITIONAL_ONLY]: ADDITIONAL_ONLY,
  [SaleEditLevelEnum.FISCAL_DATA]: FISCAL_DATA,
  [SaleEditLevelEnum.FULL]: FULL
};

export function saleIsImmutableField(field: SaleEditFieldName): boolean {
  return SALE_IMMUTABLE_FIELDS.has(field);
}

export function saleAllowsField(editLevel: SaleEditLevel | null | undefined, field: SaleEditFieldName): boolean {
  if (editLevel == null) {
    return true;
  }
  if (saleIsImmutableField(field)) {
    return false;
  }
  return SALE_EDIT_FIELDS_BY_LEVEL[editLevel]?.has(field) ?? false;
}

export function saleAllowsInvoicingProfileChanges(editLevel: SaleEditLevel | null | undefined): boolean {
  return editLevel != null && editLevel !== SaleEditLevelEnum.ADDITIONAL_ONLY;
}

export function saleEditLevelTooltip(editLevel: SaleEditLevel | null | undefined): string {
  switch (editLevel) {
    case SaleEditLevelEnum.ADDITIONAL_ONLY:
      return 'No editable: la venta ya está facturada';
    case SaleEditLevelEnum.FISCAL_DATA:
      return 'No editable: hay recibos pagados o el cobro es recurrente';
    default:
      return 'No editable en el estado actual de la venta';
  }
}

export function saleImmutableFieldTooltip(): string {
  return 'No se puede cambiar después de crear la venta';
}

export function saleFieldReadonlyTooltip(
  editLevel: SaleEditLevel | null | undefined,
  field: SaleEditFieldName
): string {
  if (saleIsImmutableField(field)) {
    return saleImmutableFieldTooltip();
  }
  return saleEditLevelTooltip(editLevel);
}

/** Mensaje al confirmar un cambio financiero que regenera cargos y ligas de pago. */
export function saleFullEditSaveConfirmMessage(): string {
  return 'Se cancelarán los cargos y las ligas de pago actuales y se regenerarán con las nuevas condiciones. ¿Deseas continuar?';
}
